import { LUTS_FILTER } from './lut-filters';
import { MOTION_GRAPHICS_FILTER } from './motion-graphics-filter';

// Backticks delimit string values containing spaces or punctuation in
// Typesense filter expressions. We strip both backticks (which would
// terminate the literal) and backslashes (which some parser combos treat as
// the in-literal escape character — `\`` could re-introduce a backtick).
// Real contributor names don't contain either character; this is purely a
// guardrail against rogue input. Strip rather than escape, since the
// downstream collections aren't consistent about whether `\\` survives a
// round-trip through the indexer.
export const escapeTypesenseContributorName = (name: string): string =>
  name.trim().replace(/[`\\]/g, '');

// SSR (`pages/contributors/[slug].page.tsx`) and the client-side
// `MotionGraphicsListings` both need the same filter expression so the
// prefetched cache entry hydrates correctly. Centralise the construction
// here so a stray edit to one site doesn't silently break the SSR sync.
export const buildContributorMotionGraphicsFilter = (
  contributorName: string,
  urlFilters?: string
): string => {
  const escapedName = escapeTypesenseContributorName(contributorName);
  const contributorClause = `contributor.name:=\`${escapedName}\``;
  const baseFilter = urlFilters
    ? `${urlFilters} && ${MOTION_GRAPHICS_FILTER}`
    : MOTION_GRAPHICS_FILTER;
  return `${baseFilter} && ${contributorClause}`;
};

// Same shape as the MG helper above — LUTs live in motiongraphics.v2 behind
// LUTS_FILTER, so the SSR prefetch and the client-side `LUTListings` both
// need this expression byte-for-byte identical to share a query-key cache
// entry across the hydration boundary.
export const buildContributorLutsFilter = (
  contributorName: string,
  urlFilters?: string
): string => {
  const escapedName = escapeTypesenseContributorName(contributorName);
  const contributorClause = `contributor.name:=\`${escapedName}\``;
  const baseFilter = `${LUTS_FILTER} && ${contributorClause}`;
  return urlFilters ? `${urlFilters} && ${baseFilter}` : baseFilter;
};

// sfx.v3 stores `contributor_name` as a flat field (no nested `contributor`
// object), so the SFX clause uses underscored field naming. Otherwise the
// shape mirrors the MG/LUTs helpers above so the SSR prefetch and the
// client `SfxListings` share a cache entry.
export const buildContributorSfxFilter = (
  contributorName: string,
  urlFilters?: string
): string => {
  const escapedName = escapeTypesenseContributorName(contributorName);
  const contributorClause = `contributor_name:=\`${escapedName}\``;
  return urlFilters
    ? `${urlFilters} && ${contributorClause}`
    : contributorClause;
};

// videos.v1 uses the same flat `contributor_name` field as sfx.v3 — its
// `contributor_slug` is sometimes empty or out-of-sync with contributors.v1,
// so name is the canonical join key (matching what the contributor page's
// client-side stock-video block uses). Shared by SSR prefetch and the client
// listing.
export const buildContributorStockVideoFilter = (
  contributorName: string,
  urlFilters?: string
): string => {
  const escapedName = escapeTypesenseContributorName(contributorName);
  const contributorClause = `contributor_name:=\`${escapedName}\``;
  return urlFilters
    ? `${urlFilters} && ${contributorClause}`
    : contributorClause;
};
